/**
 * Cart Manager - Centralized Cart Management System
 * Provides robust, validated cart operations with automatic sync across the project
 */

class CartManager {
  constructor() {
    // Configuration
    this.config = {
      FREE_SHIPPING_THRESHOLD:
        window.CART_CONFIG?.FREE_SHIPPING_THRESHOLD || 500000, // Configurable from theme settings
      API_ENDPOINTS: {
        CART: "/cart.js",
        ADD: "/cart/add.js",
        CHANGE: "/cart/change.js",
        UPDATE: "/cart/update.js",
      },
      UPDATE_DEBOUNCE: 1000, // Increased from 300ms to 1000ms to prevent rapid calls
      MAX_RETRIES: 3,
      RETRY_DELAY: 1000,
      DEBUG: false,
    };

    // State management
    this.state = {
      cart: null,
      isLoading: false,
      lastUpdate: null,
      pendingUpdates: new Map(), // (currently unused but kept for compatibility)
      updateQueue: new Map(),
      processingQueue: new Set(),
      observers: new Set(),
      isInitialized: false,
      isFetching: false, // Flag to prevent multiple simultaneous fetches
      isRefreshingContent: false, // Flag to prevent conflicts during content refresh
      isQuantityUpdate: false, // Flag to prevent content refresh during quantity updates
      forceRefreshAfterUpdate: false, // force drawer refresh for removals
      isAdding: false, // prevent double add-submit cycles
      isBundleUpgrade: false, // Flag to prevent content refresh during bundle upgrades
    };

    // Internal guards
    this._selfOriginating = false; // guard to ignore our own intercepted requests
    this._fetchSeq = 0; // monotonically increasing fetch token to drop stale responses
    this._fetchPromise = null; // single-flight promise
    this._observer = null; // mutation observer ref
    this._suspendObserver = false; // suspend flag during DOM swaps

    // DOM references
    this.elements = {
      drawer: null,
      overlay: null,
      body: null,
      count: null,
      subtotal: null,
      freeShippingText: null,
      freeShippingBar: null,
      checkoutButton: null,
    };

    // Bind methods
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleCartNote = this.handleCartNote.bind(this);
    this.handleCheckout = this.handleCheckout.bind(this);
    this.handleQuantityInput = this.handleQuantityInput.bind(this);
    this.handleQuantityBlur = this.handleQuantityBlur.bind(this);
    this.handleQuantityKeydown = this.handleQuantityKeydown.bind(this);

    // Debounced helpers
    this.debouncedUpdateCartNote = this.debounce(
      (val) => this.updateCartNote(val),
      600
    );

    // Initialize
    this.init();

    // Initialize debounced function after constructor setup
    this.debouncedFetchCart = this.debounce(
      this.fetchCart.bind(this),
      this.config.UPDATE_DEBOUNCE
    );
  }

  log(...args) {
    if (this.config.DEBUG) console.log("[CartManager]", ...args);
  }

  /**
   * Initialize the cart manager
   */
  async init() {
    try {
      // Wait for DOM to be ready
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
          this.setup();
          this.ensureFreeShippingUI();
        });
      } else {
        this.setup();
        this.ensureFreeShippingUI();
      }

      // Initial cart fetch
      await this.fetchCart();
      this.state.isInitialized = true;

      // Setup event listeners
      this.setupEventListeners();

      // Setup mutation observer for dynamic content
      this.setupMutationObserver();

      this.log("Cart Manager initialized successfully");
    } catch (error) {
      console.error("Cart Manager initialization failed:", error);
    }
  }

  /**
   * Setup DOM references and initial state
   */
  setup() {
    this.elements.drawer = document.getElementById("CartDrawer");
    this.elements.overlay = document.getElementById("CartDrawerOverlay");
    this.elements.body = document.getElementById("CartDrawerBody");
    this.elements.count = document.querySelector(".cart-drawer__count");
    this.elements.subtotal = document.querySelector("[data-subtotal]");
    this.elements.freeShippingText = document.querySelector(
      ".cart-drawer__fs-text"
    );
    this.elements.freeShippingBar = document.querySelector(".progress__bar");
    this.elements.checkoutButton = document.querySelector(
      "[data-checkout-button]"
    );

    // Validate critical elements
    if (!this.elements.drawer) {
      console.warn("Cart drawer element not found");
    }
  }

  /**
   * Ensure the free-shipping UI exists (even when cart is empty)
   */
  ensureFreeShippingUI() {
    if (!this.elements.drawer) return;

    // If both elements already exist, nothing to do
    const hasText = !!this.elements.freeShippingText;
    const hasBar = !!this.elements.freeShippingBar;
    if (hasText && hasBar) return;

    // Prefer putting the FS block in the footer; fallback to drawer
    const footer =
      this.elements.drawer.querySelector(".cart-drawer__footer") ||
      this.elements.drawer;
    let fsContainer = footer.querySelector(".cart-drawer__fs");

    if (!fsContainer) {
      fsContainer = document.createElement("div");
      fsContainer.className = "cart-drawer__fs";
      fsContainer.innerHTML = `
                <div class="cart-drawer__fs-text"></div>
                <div class="progress"><div class="progress__bar" style="width:0%"></div></div>
            `;
      // Insert at the top of footer for visibility
      footer.insertBefore(fsContainer, footer.firstChild || null);
    } else {
      // Make sure inner structure exists
      if (!fsContainer.querySelector(".cart-drawer__fs-text")) {
        const t = document.createElement("div");
        t.className = "cart-drawer__fs-text";
        fsContainer.prepend(t);
      }
      if (!fsContainer.querySelector(".progress__bar")) {
        const p = document.createElement("div");
        p.className = "progress";
        p.innerHTML = `<div class="progress__bar" style="width:0%"></div>`;
        fsContainer.appendChild(p);
      }
    }

    // Refresh references
    this.elements.freeShippingText = this.elements.drawer.querySelector(
      ".cart-drawer__fs-text"
    );
    this.elements.freeShippingBar =
      this.elements.drawer.querySelector(".progress__bar");
  }

  /**
   * Setup event listeners for cart operations
   */
  setupEventListeners() {
    // Cart drawer open/close
    document.addEventListener("click", (e) => {
      if (e.target.closest("[data-open-cart]")) {
        e.preventDefault();
        this.openDrawer();
      }
      if (
        e.target.closest("[data-cart-drawer-close]") ||
        (this.elements.overlay && e.target.closest("#CartDrawerOverlay"))
      ) {
        e.preventDefault();
        this.closeDrawer();
      }
    });

    // Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.closeDrawer();
    });

    // Add to cart forms
    document.addEventListener("submit", this.handleAddToCart, true);

    // Quantity controls
    document.addEventListener("click", this.handleQuantityChange);
    document.addEventListener("input", this.handleQuantityInput);
    document.addEventListener("blur", this.handleQuantityBlur, true);
    document.addEventListener("keydown", this.handleQuantityKeydown);

    // Cart note
    document.addEventListener("input", this.handleCartNote);

    // Checkout button
    document.addEventListener("click", this.handleCheckout);

    // Network interception for other cart operations
    this.interceptNetworkRequests();
  }

  /**
   * Setup mutation observer for dynamic content changes
   *
   * Note: We only process mutations when the drawer is OPEN
   * (we return early when aria-hidden === 'true'). We also skip
   * processing while _suspendObserver is true to avoid loops.
   */
  setupMutationObserver() {
    if (!this.elements.drawer) return;

    if (this._observer) {
      try {
        this._observer.disconnect();
      } catch {}
      this._observer = null;
    }

    const observer = new MutationObserver((mutations) => {
      // Skip if drawer is closed or observer suspended
      if (this._suspendObserver) return;
      if (this.elements.drawer.getAttribute("aria-hidden") === "true") {
        return;
      }

      // Add a small delay to batch mutations together
      clearTimeout(this._mutationTimeout);
      this._mutationTimeout = setTimeout(() => {
        let shouldRefresh = false;
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            // Check if the mutation is significant enough to warrant a cart refresh
            const addedNodes = Array.from(mutation.addedNodes);
            const removedNodes = Array.from(mutation.removedNodes);

            if (addedNodes.length > 0 || removedNodes.length > 0) {
              // Check if the changes are in the cart body (actual items)
              const hasSignificantChanges =
                addedNodes.some(
                  (node) =>
                    node.nodeType === Node.ELEMENT_NODE &&
                    (node.classList?.contains("cart-drawer__item") ||
                      node.querySelector?.(".cart-drawer__item"))
                ) ||
                removedNodes.some(
                  (node) =>
                    node.nodeType === Node.ELEMENT_NODE &&
                    (node.classList?.contains("cart-drawer__item") ||
                      node.querySelector?.(".cart-drawer__item"))
                );

              if (hasSignificantChanges) {
                shouldRefresh = true;
              }
            }
          }
        });

        // Only refresh cart if there were significant changes and we're not already fetching
        if (shouldRefresh && !this.state.isFetching) {
          this.debouncedFetchCart();
        }
      }, 100); // 100ms delay to batch mutations
    });

    observer.observe(this.elements.drawer, {
      childList: true,
      subtree: true,
      attributes: false, // Don't watch attribute changes to reduce noise
      characterData: false, // Don't watch text changes to reduce noise
    });

    this._observer = observer;
  }

  /**
   * Intercept network requests to catch cart operations from other scripts
   */
  interceptNetworkRequests() {
    try {
      // Patch only once globally to avoid double-wrapping
      if (window.__CartInterceptPatched) return;
      window.__CartInterceptPatched = true;

      const mgr = this; // capture the CartManager instance

      // Intercept fetch
      const originalFetch = window.fetch;
      const wrappedFetch = async function (resource, init) {
        const response = await originalFetch(resource, init);
        try {
          const url =
            typeof resource === "string" ? resource : resource?.url || "";
          if (
            mgr.isCartOperation(url) &&
            response.ok &&
            !mgr._selfOriginating
          ) {
            await mgr.handleExternalCartUpdate();
          }
        } catch (error) {
          console.warn("Error intercepting fetch:", error);
        }
        return response;
      };
      try {
        // Preserve enumerable props if any
        Object.assign(wrappedFetch, originalFetch);
      } catch {}
      window.fetch = wrappedFetch;

      // Intercept XMLHttpRequest
      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url) {
        this.__isCartOperation = mgr.isCartOperation(url);
        return originalOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function () {
        if (this.__isCartOperation) {
          this.addEventListener("load", async () => {
            try {
              if (
                this.status >= 200 &&
                this.status < 300 &&
                !mgr._selfOriginating
              ) {
                await mgr.handleExternalCartUpdate();
              }
            } catch (error) {
              console.warn("Error intercepting XHR:", error);
            }
          });
        }
        return originalSend.apply(this, arguments);
      };
    } catch (error) {
      console.warn("Network interception setup failed:", error);
    }
  }

  /**
   * Check if URL is a cart operation
   */
  isCartOperation(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?/i.test(url);
  }

  /**
   * Handle external cart updates
   */
  async handleExternalCartUpdate() {
    try {
      await this.fetchCart(true);
      this.notifyObservers("cart:external-update");
    } catch (error) {
      console.error("Failed to handle external cart update:", error);
    }
  }

  /**
   * Internal helper to mark requests as self-originating
   */
  async _cartRequest(fetcher) {
    this._selfOriginating = true;
    try {
      return await fetcher();
    } finally {
      this._selfOriginating = false;
    }
  }

  /**
   * Fetch cart data from Shopify API with single-flight & stale-response guard
   */
  async fetchCart(force = false) {
    // Use single-flight promise unless forced
    if (this._fetchPromise && !force) {
      return this._fetchPromise;
    }

    const run = async () => {
      // Prevent multiple simultaneous requests (still allow force to bypass early return)
      if ((this.state.isLoading || this.state.isFetching) && !force) {
        return this.state.cart;
      }

      const seq = ++this._fetchSeq;
      this.state.isLoading = true;
      this.state.isFetching = true;

      try {
        const response = await fetch(this.config.API_ENDPOINTS.CART, {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          throw new Error(`Cart fetch failed: ${response.status}`);
        }

        const cartData = await response.json();

        // Validate cart data
        if (!this.validateCartData(cartData)) {
          throw new Error("Invalid cart data received");
        }

        // Stale response guard
        if (seq !== this._fetchSeq) {
          return this.state.cart; // ignore older response
        }

        // Update state
        this.state.cart = cartData;
        this.state.lastUpdate = Date.now();

        // Update UI
        await this.updateUI();

        // Notify observers
        this.notifyObservers("cart:fetched", cartData);

        return cartData;
      } catch (error) {
        console.error("Cart fetch failed:", error);
        this.notifyObservers("cart:error", error);
        throw error;
      } finally {
        this.state.isLoading = false;
        this.state.isFetching = false;
        this._fetchPromise = null;
      }
    };

    this._fetchPromise = run();
    return this._fetchPromise;
  }

  /**
   * Validate cart data structure
   */
  validateCartData(cartData) {
    if (!cartData || typeof cartData !== "object") return false;

    const requiredFields = ["item_count", "total_price", "items"];
    return requiredFields.every(
      (field) =>
        Object.prototype.hasOwnProperty.call(cartData, field) &&
        (field === "items"
          ? Array.isArray(cartData[field])
          : typeof cartData[field] === "number")
    );
  }

  /**
   * Update UI with current cart data
   */
  async updateUI() {
    if (!this.state.cart) return;

    const { cart } = this.state;

    // Update cart count
    if (this.elements.count) {
      this.elements.count.textContent = `(${cart.item_count})`;
    }

    // Update subtotal with consistent formatting
    if (this.elements.subtotal) {
      this.elements.subtotal.textContent = this.formatMoney(cart.total_price);
    }

    // Ensure FS UI exists before trying to update it
    this.ensureFreeShippingUI();

    // Update free shipping progress
    this.updateFreeShippingProgress(cart);

    // Update checkout button
    this.updateCheckoutButton();

    // Refresh drawer content if it's open
    // Skip refresh during bundle upgrades as it's handled explicitly in handleBundleUpgrade
    if (
      this.elements.drawer &&
      this.elements.drawer.getAttribute("aria-hidden") === "false" &&
      !this.state.isBundleUpgrade
    ) {
      const shouldRefreshDrawer =
        !this.state.isQuantityUpdate || this.state.forceRefreshAfterUpdate;

      if (shouldRefreshDrawer) {
        await this.refreshDrawerContent();
        // one-shot: clear after we refreshed (ensures removals disappear)
        this.state.forceRefreshAfterUpdate = false;
      }
    }

    // Clear all loading states
    this.clearLoadingStates();
  }

  /**
   * Refresh drawer content with current cart data
   */
  async refreshDrawerContent() {
    if (!this.elements.drawer) return;

    // Set flag to prevent conflicts during refresh
    this.state.isRefreshingContent = true;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set("section_id", "cart-drawer");

      const response = await fetch(url.toString(), {
        headers: { Accept: "text/html" },
      });

      if (!response.ok) throw new Error("Drawer refresh failed");

      const html = await response.text();
      const template = document.createElement("div");
      template.innerHTML = html;

      const freshDrawer = template.querySelector("#CartDrawer");
      if (freshDrawer) {
        this._suspendObserver = true; // prevent observer feedback loop
        try {
          // Update header
          const freshHeader = freshDrawer.querySelector(".cart-drawer__header");
          const currentHeader = this.elements.drawer.querySelector(
            ".cart-drawer__header"
          );
          if (freshHeader && currentHeader) {
            if (currentHeader.innerHTML !== freshHeader.innerHTML) {
              currentHeader.innerHTML = freshHeader.innerHTML;
            }
          }

          // Update body
          const freshBody = freshDrawer.querySelector("#CartDrawerBody");
          if (freshBody && this.elements.body) {
            if (this.elements.body.innerHTML !== freshBody.innerHTML) {
              this.elements.body.innerHTML = freshBody.innerHTML;
            }
          }

          // Update footer
          const freshFooter = freshDrawer.querySelector(".cart-drawer__footer");
          const currentFooter = this.elements.drawer.querySelector(
            ".cart-drawer__footer"
          );
          if (freshFooter && currentFooter) {
            if (currentFooter.innerHTML !== freshFooter.innerHTML) {
              currentFooter.innerHTML = freshFooter.innerHTML;
            }
          }
        } finally {
          this._suspendObserver = false;
        }

        // Re-initialize elements after content update
        this.setup();
        // Make sure FS UI exists even if theme omitted it in empty state
        this.ensureFreeShippingUI();
        // Re-apply FS numbers after DOM swap
        if (this.state.cart) this.updateFreeShippingProgress(this.state.cart);
      }
    } catch (error) {
      console.error("Drawer content refresh failed:", error);
    } finally {
      // Always clear the flag, even if refresh fails
      this.state.isRefreshingContent = false;
    }
  }

  /**
   * Clear all loading states
   */
  clearLoadingStates() {
    // Clear updating classes from quantity inputs
    const updatingInputs = this.elements.drawer?.querySelectorAll(
      ".qty__input.updating"
    );
    if (updatingInputs) {
      updatingInputs.forEach((input) => {
        input.classList.remove("updating");
        input.disabled = false;
      });
    }

    // Clear updating classes from remove buttons
    const updatingRemoveBtns = this.elements.drawer?.querySelectorAll(
      "[data-qty-remove].updating"
    );
    if (updatingRemoveBtns) {
      updatingRemoveBtns.forEach((btn) => {
        btn.classList.remove("updating");
      });
    }

    // Clear updating class from cart count
    if (this.elements.count) {
      this.elements.count.classList.remove("updating");
    }
  }

  /**
   * Update free shipping progress bar
   */
  updateFreeShippingProgress(cart) {
    // Always ensure the elements exist before updating
    this.elements.freeShippingText =
      this.elements.drawer?.querySelector(".cart-drawer__fs-text") ||
      this.elements.freeShippingText;
    this.elements.freeShippingBar =
      this.elements.drawer?.querySelector(".progress__bar") ||
      this.elements.freeShippingBar;
    if (!this.elements.freeShippingText || !this.elements.freeShippingBar)
      return;

    const current = cart.total_price || 0;
    const threshold = this.config.FREE_SHIPPING_THRESHOLD;
    const remain = Math.max(0, threshold - current);

    if (current < threshold) {
      this.elements.freeShippingText.innerHTML = `Ücretsiz kargo için <strong>${this.formatMoney(
        remain
      )}</strong> kaldı`;
    } else {
      this.elements.freeShippingText.textContent =
        "Ücretsiz kargo hakkı kazandınız 🎉";
    }

    const percentage =
      threshold > 0
        ? Math.min(100, Math.round((current * 100) / threshold))
        : 0;
    this.elements.freeShippingBar.style.width = `${percentage}%`;
  }

  /**
   * Update checkout button state
   */
  updateCheckoutButton() {
    if (!this.elements.checkoutButton) return;

    // Ensure proper href
    if (
      !this.elements.checkoutButton.href ||
      !this.elements.checkoutButton.href.includes("checkout")
    ) {
      this.elements.checkoutButton.href = "/checkout";
    }

    // Disable if cart is empty
    if (this.state.cart && this.state.cart.item_count === 0) {
      this.elements.checkoutButton.disabled = true;
      this.elements.checkoutButton.classList.add("disabled");
    } else {
      this.elements.checkoutButton.disabled = false;
      this.elements.checkoutButton.classList.remove("disabled");
    }
  }

  /**
   * Format money amount consistently
   */
  formatMoney(cents) {
    try {
      // Always use consistent TL formatting
      const amount = (cents / 100).toFixed(2);
      return amount + "TL";
    } catch (error) {
      // Fallback to simple format
      return (cents / 100).toFixed(2) + "TL";
    }
  }

  /**
   * Add item to cart
   */
  async addToCart(formData) {
    if (this.state.isAdding) {
      return false;
    }

    this.state.isAdding = true;

    // Set flag to prevent content refresh during add to cart
    this.state.isQuantityUpdate = true;

    try {
      const response = await this._cartRequest(() =>
        fetch(this.config.API_ENDPOINTS.ADD, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        })
      );

      if (!response.ok) {
        throw new Error(`Add to cart failed: ${response.status}`);
      }

      // ensure drawer content refreshes even while isQuantityUpdate is true
      this.state.forceRefreshAfterUpdate = true;

      // Open drawer
      this.openDrawer();

      // Fetch updated cart
      await this.fetchCart(true);

      // Notify observers
      this.notifyObservers("cart:item-added");

      return true;
    } catch (error) {
      console.error("Add to cart failed:", error);
      this.notifyObservers("cart:error", error);
      throw error;
    } finally {
      // Clear the flag after a short delay to allow for UI updates
      this.state.isQuantityUpdate = false;
      this.state.isAdding = false;
    }
  }

  /**
   * Change item quantity
   */
  async changeQuantity(line, quantity) {
    // Validate input
    if (
      typeof line !== "number" ||
      typeof quantity !== "number" ||
      quantity < 0
    ) {
      throw new Error("Invalid quantity parameters");
    }

    // Set flag to prevent content refresh during quantity updates
    this.state.isQuantityUpdate = true;

    // If this is effectively a removal, force a drawer refresh
    const isRemoval = quantity === 0;
    if (isRemoval) this.state.forceRefreshAfterUpdate = true;

    // Track upgrade visibility before the change
    const prevShouldUpgrade = this.shouldShowBundleUpgrade(this.state.cart);

    try {
      const response = await this._cartRequest(() =>
        fetch(this.config.API_ENDPOINTS.CHANGE, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ line, quantity }),
        })
      );

      if (!response.ok) {
        throw new Error(`Quantity change failed: ${response.status}`);
      }

      // Fetch updated cart
      await this.fetchCart(true);

      // Notify observers
      // If upgrade visibility changed, refresh the drawer now (no timeouts)
      // Skip refresh during bundle upgrades as it's handled explicitly
      const currShouldUpgrade = this.shouldShowBundleUpgrade(this.state.cart);
      if (
        prevShouldUpgrade !== currShouldUpgrade &&
        this.elements.drawer &&
        this.elements.drawer.getAttribute("aria-hidden") === "false" &&
        !this.state.isBundleUpgrade
      ) {
        await this.refreshDrawerContent();
      }

      // Notify observers
      this.notifyObservers("cart:quantity-changed", { line, quantity });

      return true;
    } catch (error) {
      console.error("Quantity change failed:", error);
      this.notifyObservers("cart:error", error);
      throw error;
    } finally {
      // Clear the flag after a short delay to allow for UI updates
      this.state.isQuantityUpdate = false;
    }
  }

  /**
   * Update cart note
   */
  async updateCartNote(note) {
    // Set flag to prevent content refresh during note updates
    this.state.isQuantityUpdate = true;

    try {
      const response = await this._cartRequest(() =>
        fetch(this.config.API_ENDPOINTS.UPDATE, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ note }),
        })
      );

      if (!response.ok) {
        throw new Error(`Note update failed: ${response.status}`);
      }

      // Fetch updated cart
      await this.fetchCart(true);

      // Notify observers
      this.notifyObservers("cart:note-updated", { note });

      return true;
    } catch (error) {
      console.error("Note update failed:", error);
      this.notifyObservers("cart:error", error);
      throw error;
    } finally {
      // Clear the flag after a short delay to allow for UI updates
      this.state.isQuantityUpdate = false;
    }
  }

  /**
   * Handle add to cart form submission
   */
  async handleAddToCart(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = (form.action || "").toString();
    const isAddToCart =
      /\/cart\/add(\.js)?/i.test(action) || form.hasAttribute("data-ajax-add");
    if (!isAddToCart) return;

    e.preventDefault();

    // ⬇️ NEW: ignore if an add is already running
    if (this.state.isAdding) {
      this.log("Add blocked: add already in progress");
      return;
    }

    try {
      this.state.isAdding = true;
      const formData = new FormData(form);
      await this.addToCart(formData);
    } catch (error) {
      console.error("Add to cart failed:", error);
    } finally {
      this.state.isAdding = false;
    }
  }

  /**
   * Handle quantity change clicks
   */
  async handleQuantityChange(e) {
    const decreaseBtn = e.target.closest("[data-qty-decrease]");
    const increaseBtn = e.target.closest("[data-qty-increase]");
    const removeBtn = e.target.closest("[data-qty-remove]");

    if (!decreaseBtn && !increaseBtn && !removeBtn) return;

    e.preventDefault();

    const button = decreaseBtn || increaseBtn || removeBtn;
    the_line: {
      const line = Number(button.getAttribute("data-line"));
      const input = this.elements.drawer?.querySelector(
        `.qty__input[data-line="${line}"]`
      );
      if (!input) break the_line;

      let currentQty = Number(input.value || 1);
      let newQty = currentQty;

      if (decreaseBtn) newQty = Math.max(0, currentQty - 1);
      if (increaseBtn) newQty = currentQty + 1;
      if (removeBtn) newQty = 0;

      try {
        // Optimistic update
        this.updateQuantityOptimistically(line, newQty);

        // Queue actual update
        this.queueQuantityUpdate(line, newQty);
      } catch (error) {
        console.error("Quantity change failed:", error);
        // Revert optimistic update on error
        this.revertOptimisticUpdate(line, currentQty);
      }
    }
  }

  /**
   * Revert optimistic update on error
   */
  revertOptimisticUpdate(line, originalQty) {
    const input = this.elements.drawer?.querySelector(
      `.qty__input[data-line="${line}"]`
    );
    if (input) {
      input.value = originalQty;
      input.classList.remove("updating");
      input.disabled = false;
    }

    const removeBtn = this.elements.drawer?.querySelector(
      `[data-qty-remove][data-line="${line}"]`
    );
    if (removeBtn) {
      removeBtn.classList.remove("updating");
    }

    // Revert cart count
    if (this.state.cart) {
      const currentCount = this.state.cart.item_count;
      this.updateCartCountOptimistically(currentCount);
    }
  }

  /**
   * Handle quantity input changes
   */
  handleQuantityInput(e) {
    const input = e.target.closest(".qty__input[data-line]");
    if (!input) return;

    const line = Number(input.getAttribute("data-line"));
    const quantity = Math.max(0, Number(input.value || 0));

    // Optimistic update
    this.updateQuantityOptimistically(line, quantity);

    // Queue actual update
    this.queueQuantityUpdate(line, quantity);
  }

  /**
   * Handle quantity input blur
   */
  async handleQuantityBlur(e) {
    const input = e.target.closest(".qty__input[data-line]");
    if (!input) return;

    const line = Number(input.getAttribute("data-line"));
    const quantity = Math.max(0, Number(input.value || 0));

    try {
      if (quantity === 0) {
        await this.changeQuantity(line, 0);
      } else {
        await this.changeQuantity(line, quantity);
      }
    } catch (error) {
      console.error("Quantity update failed:", error);
    }
  }

  /**
   * Handle quantity input keydown
   */
  handleQuantityKeydown(e) {
    if (e.key !== "Enter") return;

    const input = e.target.closest(".qty__input[data-line]");
    if (input) {
      input.blur();
    }
  }

  /**
   * Handle cart note updates (debounced to reduce spam)
   */
  async handleCartNote(e) {
    const noteInput = e.target.closest("[data-cart-note]");
    if (!noteInput) return;

    try {
      this.debouncedUpdateCartNote(noteInput.value);
    } catch (error) {
      console.error("Cart note update failed:", error);
    }
  }

  /**
   * Handle checkout button click
   */
  handleCheckout(e) {
    const checkoutBtn = e.target.closest("[data-checkout-button]");
    if (!checkoutBtn) return;

    e.preventDefault();
    e.stopPropagation();

    const href = checkoutBtn.href || "/checkout";
    window.location.href = href;
    return false;
  }

  /**
   * Optimistic quantity update for better UX
   */
  updateQuantityOptimistically(line, quantity) {
    const input = this.elements.drawer?.querySelector(
      `.qty__input[data-line="${line}"]`
    );
    const removeBtn = this.elements.drawer?.querySelector(
      `[data-qty-remove][data-line="${line}"]`
    );

    if (input) {
      input.value = quantity;
      input.classList.add("updating");
      input.disabled = true; // Disable input while updating
    }

    if (removeBtn) {
      removeBtn.classList.add("updating");
    }

    // Update cart count optimistically
    if (this.state.cart) {
      const currentCount = this.state.cart.item_count;
      const originalQty = this.getLineItemQuantity(line);
      const newCount = currentCount - originalQty + quantity;

      this.updateCartCountOptimistically(newCount);
    }
  }

  /**
   * Get current quantity for a line item
   */
  getLineItemQuantity(line) {
    if (!this.state.cart || !this.state.cart.items) return 0;

    const item = this.state.cart.items[line - 1];
    return item ? item.quantity : 0;
  }

  /**
   * Update cart count optimistically
   */
  updateCartCountOptimistically(count) {
    if (this.elements.count) {
      this.elements.count.textContent = `(${count})`;
      this.elements.count.classList.add("updating");

      // Remove updating class after delay
      setTimeout(() => {
        this.elements.count.classList.remove("updating");
      }, 1000);
    }

    // Notify header
    this.notifyObservers("cart:count-updated", { count });
  }

  /**
   * Queue quantity updates to prevent API spam
   */
  queueQuantityUpdate(line, quantity) {
    this.state.updateQueue.set(line, quantity);

    if (!this.state.processingQueue.has(line)) {
      this.processQuantityUpdate(line);
    }
  }

  /**
   * Process queued quantity updates
   */
  async processQuantityUpdate(line) {
    this.state.processingQueue.add(line);

    try {
      while (this.state.updateQueue.has(line)) {
        const quantity = this.state.updateQueue.get(line);
        this.state.updateQueue.delete(line);

        try {
          await this.changeQuantity(line, quantity);
        } catch (error) {
          console.error(`Quantity update failed for line ${line}:`, error);
          // Don't break the loop, continue with other updates
        }

        // Small delay to prevent overwhelming the API
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    } finally {
      this.state.processingQueue.delete(line);
      // Ensure loading states are cleared
      this.clearLoadingStates();
    }
  }

  /**
   * Open cart drawer
   */
  openDrawer() {
    if (!this.elements.drawer || !this.elements.overlay) return;

    // Show overlay
    this.elements.overlay.removeAttribute("hidden");
    this.elements.overlay.offsetHeight; // Force reflow

    // Add classes for transitions
    document.documentElement.classList.add("cart-drawer-open");
    document.body.classList.add("cart-drawer-open");

    // Show drawer
    this.elements.drawer.setAttribute("aria-hidden", "false");

    // Only fetch cart if it's stale (older than 30 seconds) or doesn't exist
    const shouldFetchCart =
      !this.state.cart ||
      !this.state.lastUpdate ||
      Date.now() - this.state.lastUpdate > 30000;

    if (shouldFetchCart) {
      this.debouncedFetchCart();
    }

    // Ensure FS UI exists when opening
    this.ensureFreeShippingUI();
    if (this.state.cart) this.updateFreeShippingProgress(this.state.cart);

    // Notify observers
    this.notifyObservers("cart:drawer-opened");
  }

  /**
   * Close cart drawer
   */
  closeDrawer() {
    if (!this.elements.drawer || !this.elements.overlay) return;

    // Remove classes
    document.documentElement.classList.remove("cart-drawer-open");
    document.body.classList.remove("cart-drawer-open");

    // Hide drawer
    this.elements.drawer.setAttribute("aria-hidden", "true");

    // Hide overlay with delay for transition
    setTimeout(() => {
      this.elements.overlay.setAttribute("hidden", "");
    }, 300);

    // Clear any pending mutation timeouts
    if (this._mutationTimeout) {
      clearTimeout(this._mutationTimeout);
      this._mutationTimeout = null;
    }

    // Clear pending updates
    this.state.pendingUpdates.clear();
    this.state.updateQueue.clear();
    this.state.processingQueue.clear();

    // Notify observers
    this.notifyObservers("cart:drawer-closed");
  }

  /**
   * Debounce utility function
   */
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  /**
   * Add observer for cart events
   */
  addObserver(callback) {
    this.state.observers.add(callback);
    return () => this.state.observers.delete(callback);
  }

  /**
   * Remove observer
   */
  removeObserver(callback) {
    this.state.observers.delete(callback);
  }

  /**
   * Notify all observers of cart events
   */
  notifyObservers(event, data = null) {
    this.state.observers.forEach((callback) => {
      try {
        callback(event, data);
      } catch (error) {
        console.error("Observer callback error:", error);
      }
    });

    // Also dispatch custom events for backward compatibility
    document.dispatchEvent(new CustomEvent(event, { detail: data }));
  }

  /**
   * Get current cart data
   */
  getCart() {
    return this.state.cart;
  }

  /**
   * Get cart item count
   */
  getItemCount() {
    return this.state.cart?.item_count || 0;
  }

  /**
   * Get cart total price
   */
  getTotalPrice() {
    return this.state.cart?.total_price || 0;
  }

  /**
   * Check if cart is empty
   */
  isEmpty() {
    return !this.state.cart || this.state.cart.item_count === 0;
  }

  /**
   * Check if cart is loading
   */
  isLoading() {
    return this.state.isLoading;
  }

  /**
   * Check if cart manager is initialized
   */
  isInitialized() {
    return this.state.isInitialized;
  }

  /**
   * Force refresh cart data
   */
  async refresh() {
    return this.fetchCart(true);
  }

  /**
   * Manually refresh cart data (useful for external triggers)
   */
  async manualRefresh() {
    // Only refresh if not already fetching and drawer is open
    if (
      !this.state.isFetching &&
      this.elements.drawer &&
      this.elements.drawer.getAttribute("aria-hidden") === "false"
    ) {
      return this.fetchCart(true);
    }
    return this.state.cart;
  }

  /**
   * Clear cart
   */
  async clearCart() {
    // Set flag to prevent content refresh during cart clearing
    this.state.isQuantityUpdate = true;

    try {
      const response = await this._cartRequest(() =>
        fetch("/cart/clear.js", {
          method: "POST",
          headers: { Accept: "application/json" },
        })
      );

      if (!response.ok) {
        throw new Error(`Cart clear failed: ${response.status}`);
      }

      // Fetch updated cart
      await this.fetchCart(true);

      // Notify observers
      this.notifyObservers("cart:cleared");

      return true;
    } catch (error) {
      console.error("Cart clear failed:", error);
      this.notifyObservers("cart:error", error);
      throw error;
    } finally {
      // Clear the flag after a short delay to allow for UI updates
      this.state.isQuantityUpdate = false;
    }
  }

  /**
   * Handle bundle upgrade - replace single non-bundle item with bundle variant
   * Optimized to minimize cart fetches
   */
  async handleBundleUpgrade(bundleVariantId) {
    if (this.state.isBundleUpgrade) return;

    this.state.isBundleUpgrade = true;

    try {
      // Set loading state for upgrade button
      this.setBundleUpgradeLoading(true);

      // Perform both operations without individual cart fetches
      await this._performBundleUpgradeOperations(bundleVariantId);

      // Single cart fetch after both operations complete
      await this.fetchCart(true);

      // Single drawer refresh after cart is updated
      if (
        this.elements.drawer &&
        this.elements.drawer.getAttribute("aria-hidden") === "false"
      ) {
        await this.refreshDrawerContent();
      }

      // Notify observers
      this.notifyObservers("cart:bundle-upgraded", { bundleVariantId });
    } catch (error) {
      console.error("Bundle upgrade failed:", error);
      this.notifyObservers("cart:error", error);
      throw error;
    } finally {
      // Clear loading state after delay
      this.state.isBundleUpgrade = false;
      this.setBundleUpgradeLoading(false);
    }
  }

  /**
   * Perform bundle upgrade operations without individual cart fetches
   */
  async _performBundleUpgradeOperations(bundleVariantId) {
    // First remove the current item (line 1) - without cart fetch
    const removeResponse = await this._cartRequest(() =>
      fetch(this.config.API_ENDPOINTS.CHANGE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ line: 1, quantity: 0 }),
      })
    );

    if (!removeResponse.ok) {
      throw new Error(`Remove item failed: ${removeResponse.status}`);
    }

    // Then add the bundle variant - without cart fetch
    const addFormData = new FormData();
    addFormData.append("id", bundleVariantId.toString());
    addFormData.append("quantity", "1");

    const addResponse = await this._cartRequest(() =>
      fetch(this.config.API_ENDPOINTS.ADD, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: addFormData,
      })
    );

    if (!addResponse.ok) {
      throw new Error(`Add bundle failed: ${addResponse.status}`);
    }
  }

  /**
   * Set loading state for bundle upgrade button and show full loader
   */
  setBundleUpgradeLoading(isLoading) {
    const upgradeBtn = this.elements.drawer?.querySelector(
      "[data-bundle-upgrade]"
    );
    const cartBody = this.elements.body;

    if (isLoading) {
      // Update button state
      if (upgradeBtn) {
        upgradeBtn.disabled = true;
        upgradeBtn.classList.add("updating");
        upgradeBtn.textContent = "Güncelleniyor...";
      }

      // Show full loader overlay in cart body
      if (cartBody) {
        let loader = cartBody.querySelector(".cart-loader-overlay");
        if (!loader) {
          loader = document.createElement("div");
          loader.className = "cart-loader-overlay";
          loader.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="cart-loader-spinner">
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          `;
          cartBody.appendChild(loader);
        }
        loader.style.display = "flex";
      }
    } else {
      // Update button state
      if (upgradeBtn) {
        upgradeBtn.disabled = false;
        upgradeBtn.classList.remove("updating");
        upgradeBtn.textContent = "SEPETİNİ GÜNCELE VE EKSTRA İNDİRİM KAZAN";
      }

      // Hide full loader overlay
      if (cartBody) {
        const loader = cartBody.querySelector(".cart-loader-overlay");
        if (loader) {
          loader.style.display = "none";
        }
      }
    }
  }

  /**
   * Check if cart should show bundle upgrade card
   */
  shouldShowBundleUpgrade(cart) {
    // Only show if cart has exactly one item
    if (!cart || cart.item_count !== 1) return false;

    const item = cart.items[0];
    if (!item) return false;

    // Only show if item quantity is exactly 1
    if (item.quantity !== 1) return false;

    // Check if the item is NOT a bundle (doesn't have bundle_components metafield)
    const isBundle =
      item.variant &&
      item.variant.metafields &&
      item.variant.metafields.custom &&
      item.variant.metafields.custom.bundle_components;

    // Show upgrade card only for non-bundle items
    return !isBundle;
  }

  /**
   * Get bundle variant from product
   */
  getBundleVariant(product) {
    if (!product || !product.variants) return null;

    return product.variants.find((variant) => {
      return (
        variant.metafields &&
        variant.metafields.custom &&
        variant.metafields.custom.bundle_components
      );
    });
  }
}

// Create global instance
window.CartManager = new CartManager();

// Export for module systems
if (typeof module !== "undefined" && module.exports) {
  module.exports = CartManager;
}
